import React, { useState, useEffect, useCallback } from 'react';
import { useServices } from '../../hooks/useServices';
import { useToast } from '../../contexts/ToastContext';
import { ServiceDetailsResponse } from '../../types/api-responses';
import SearchBar from '../../components/SearchBar/SearchBar';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import styles from './Services.module.css';

const Services: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
    const { services, getServices, loading, error } = useServices();
    const { showToast } = useToast();

    const fetchServices = useCallback(async () => {
        try {
            await getServices(debouncedSearchTerm || undefined);
        } catch (err) {
            showToast('Unable to load services. Please refresh or try again later.', 'error');
        }
    }, [getServices, debouncedSearchTerm, showToast]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    return (
        <div className={styles.servicesPage}>
            <div className={styles.controlsHeaderContainer}>
                <h1 className='xH1'>Services</h1>
                <div className={styles.controlsContainer}>
                    <div className={styles.searchSection}>
                        <SearchBar
                            icon={<img src="./assets/icons/magnifier.png" alt="magnifier" />}
                            placeholder="Search service name, category..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            showClearButton={searchTerm.length > 0}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.servicesListContainer}>
                {loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}>
                            <svg viewBox="0 0 50 50">
                                <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                            </svg>
                            <span>Loading services...</span>
                        </div>
                    </div>
                )}

                {error && !loading && (
                    <div className={styles.errorMessage}>
                        <p>{error}</p>
                    </div>
                )}

                {!loading && !error && services.length === 0 && (
                    <div className={styles.emptyMessage}>
                        {searchTerm ? (
                            <p>No services found matching your search criteria.</p>
                        ) : (
                            <p>No services available.</p>
                        )}
                    </div>
                )}

                <div className={styles.servicesList}>
                    {services.map((service) => (
                        <ServiceCard
                            key={service.id}
                            service={service}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

// ServiceCard component (read-only for staff)
interface ServiceCardProps {
    service: ServiceDetailsResponse;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
    const [isPricingOpen, setIsPricingOpen] = useState(false);

    const handlePricingClick = () => {
        setIsPricingOpen(!isPricingOpen);
    };

    const formatDuration = (duration: string) => {
        // Duration comes as "HH:MM:SS", format to readable
        const parts = duration.split(':');
        if (parts.length >= 2) {
            const hours = parseInt(parts[0]);
            const minutes = parseInt(parts[1]);
            if (hours > 0) {
                return `${hours}h ${minutes}m`;
            }
            return `${minutes} min`;
        }
        return duration;
    };

    return (
        <div className={`${styles.serviceCard} ${isPricingOpen ? styles.expanded : ''}`}>
            <div className={styles.serviceDetailsImageContainer}>
                <div className={styles.imageContainer}>
                    <img
                        src={service.imageUrl || './assets/images/default-service.png'}
                        alt={service.name}
                        className={styles.image}
                    />
                </div>
                <div className={styles.serviceDetails}>
                    <div className={styles.category}>{service.categoryName}</div>
                    <div className={styles.title}>{service.name}</div>
                    <div className={styles.durationBranch}>
                        <span className={styles.duration}>
                            {formatDuration(service.minDuration)} - {formatDuration(service.maxDuration)}
                        </span>
                        <span className={`${styles.status} ${service.isActive ? styles.available : styles.unavailable}`}>
                            {service.isActive ? 'Available' : 'Not Available'}
                        </span>
                    </div>
                </div>
            </div>

            <div
                onClick={handlePricingClick}
                className={`${styles.pricingButton} ${styles.arrowUpDown} ${isPricingOpen ? styles.isOpen : ''}`}
            >
                Pricing options
            </div>

            {isPricingOpen && (
                <div className={styles.pricingMenu}>
                    {service.pricingOptions.map((option, index) => (
                        <div key={index} className={styles.pricingItem}>
                            <div className={styles.optionDuration}>
                                <span className={styles.option}>{option.name}</span>
                                <span className={styles.optionDurationText}>{formatDuration(option.duration)}</span>
                            </div>
                            <div className={styles.price}>
                                <span className={styles.amount}>{option.price}</span>
                                <span className={styles.currency}>{option.currency}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Services;
